import { SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION } from "./contract";
import {
  parseSpecialistServiceAddOnQuerySelections,
  serializeSpecialistServiceAddOnQuerySelections,
} from "../serviceAddOns";
import {
  parseSpecialistServiceBundleAddOnQuerySelections,
  serializeSpecialistServiceBundleAddOnQuerySelections,
} from "../serviceBundleAddOns";
import {
  buildSpecialistProfilePath,
  buildSpecialistServicesPath,
} from "../routes";
import {
  RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY,
  RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY,
  appendResultsAppointmentIntentHandoffToHref,
  parseResultsAppointmentIntentHandoff,
} from "@/app/results/booking/handoff";

export const SPECIALIST_LOCATION_GATEWAY_HANDOFF_ID_QUERY_KEY =
  "slg_handoff_id";
export const SPECIALIST_LOCATION_GATEWAY_HANDOFF_VERSION_QUERY_KEY =
  "slg_handoff_version";
export const SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY =
  "slg_selected_assignment_id";

const OPAQUE_POINTER_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9._:-]{0,159})$/u;
const MAX_SHARED_SERIALIZED_SELECTION_LENGTH = 8_000;
const MAX_ROUTE_CATALOG_IDENTIFIER_LENGTH = 160;

export interface SpecialistLocationGatewaySearchParamsLike {
  getAll(name: string): string[];
  keys(): IterableIterator<string>;
}

export type SpecialistLocationGatewayHandoffParseResult =
  | {
      ok: true;
      handoffId: string;
      schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION;
      selectedAssignmentHint: string | null;
      resultsAppointmentIntentHandoffId: string | null;
    }
  | {
      ok: false;
      reason:
        | "duplicate-selected-assignment-hint"
        | "invalid-handoff-id"
        | "invalid-results-handoff"
        | "invalid-services-query"
        | "invalid-selected-assignment-hint"
        | "missing-handoff-id"
        | "missing-handoff-version"
        | "unsupported-handoff-version";
    };

export function isSpecialistLocationGatewayOpaquePointer(
  value: unknown,
): value is string {
  return typeof value === "string" && OPAQUE_POINTER_PATTERN.test(value);
}

/**
 * Matches shared catalog identifier semantics: Unicode, spaces, `@`, and `/`
 * remain data. Leading/trailing whitespace, commas (the list delimiter), and
 * control bytes are never silently normalized.
 */
function isCatalogIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= MAX_ROUTE_CATALOG_IDENTIFIER_LENGTH &&
    !value.includes(",") &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

/** Route identities are one segment, so separators and dot segments fail. */
function isRouteSegment(value: unknown): value is string {
  return (
    isCatalogIdentifier(value) &&
    value !== "." &&
    value !== ".." &&
    !value.includes("/") &&
    !value.includes("\\")
  );
}

function parseSpecialistLocationGatewayHandoff(
  searchParams: SpecialistLocationGatewaySearchParamsLike,
  allowedKeys: ReadonlySet<string>,
  allowSelectedAssignmentHint: boolean,
): SpecialistLocationGatewayHandoffParseResult {
  const resultsHandoffIds = searchParams.getAll(
    RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY,
  );
  const resultsHandoffVersions = searchParams.getAll(
    RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY,
  );
  const resultsHandoffMarkerPresent =
    resultsHandoffIds.length > 0 || resultsHandoffVersions.length > 0;
  const allowedKeysWithResults = new Set([
    ...allowedKeys,
    RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY,
    RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY,
  ]);
  for (const key of searchParams.keys()) {
    if (!allowedKeysWithResults.has(key)) {
      return { ok: false, reason: "invalid-handoff-id" };
    }
  }
  const resultsHandoff = resultsHandoffMarkerPresent
    ? parseResultsAppointmentIntentHandoff(searchParams, allowedKeys)
    : null;
  if (resultsHandoff && !resultsHandoff.ok) {
    return { ok: false, reason: "invalid-results-handoff" };
  }
  const handoffIds = searchParams.getAll(
    SPECIALIST_LOCATION_GATEWAY_HANDOFF_ID_QUERY_KEY,
  );
  if (handoffIds.length === 0) {
    return { ok: false, reason: "missing-handoff-id" };
  }
  if (
    handoffIds.length !== 1 ||
    !isSpecialistLocationGatewayOpaquePointer(handoffIds[0])
  ) {
    return { ok: false, reason: "invalid-handoff-id" };
  }

  const versions = searchParams.getAll(
    SPECIALIST_LOCATION_GATEWAY_HANDOFF_VERSION_QUERY_KEY,
  );
  if (versions.length === 0) {
    return { ok: false, reason: "missing-handoff-version" };
  }
  if (
    versions.length !== 1 ||
    versions[0] !== String(SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION)
  ) {
    return { ok: false, reason: "unsupported-handoff-version" };
  }

  const selectedAssignmentHints = searchParams.getAll(
    SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY,
  );
  if (!allowSelectedAssignmentHint && selectedAssignmentHints.length > 0) {
    return { ok: false, reason: "invalid-selected-assignment-hint" };
  }
  if (selectedAssignmentHints.length > 1) {
    return { ok: false, reason: "duplicate-selected-assignment-hint" };
  }
  const selectedAssignmentHint = selectedAssignmentHints[0] ?? null;
  if (
    selectedAssignmentHint !== null &&
    !isRouteSegment(selectedAssignmentHint)
  ) {
    return { ok: false, reason: "invalid-selected-assignment-hint" };
  }

  return {
    ok: true,
    handoffId: handoffIds[0]!,
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
    selectedAssignmentHint,
    resultsAppointmentIntentHandoffId:
      resultsHandoff?.ok === true ? resultsHandoff.handoffId : null,
  };
}

const BASE_HANDOFF_QUERY_KEYS = new Set([
  SPECIALIST_LOCATION_GATEWAY_HANDOFF_ID_QUERY_KEY,
  SPECIALIST_LOCATION_GATEWAY_HANDOFF_VERSION_QUERY_KEY,
]);
const SELECTED_HANDOFF_QUERY_KEYS = new Set([
  ...BASE_HANDOFF_QUERY_KEYS,
  SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY,
]);

export const SPECIALIST_LOCATION_GATEWAY_SERVICES_ALLOWED_QUERY_KEYS = [
  "appointment_mode",
  "catalog",
  "configure_service_id",
  "configure_variant_id",
  "focus_package_id",
  "focus_service_id",
  "group_booking_draft_id",
  "group_booking_draft_version",
  "packages",
  "service_addons",
  "service_bundle_addons",
  "service_variants",
  "services",
] as const;

export function parseSpecialistLocationGatewayLocationHandoff(
  searchParams: SpecialistLocationGatewaySearchParamsLike,
) {
  return parseSpecialistLocationGatewayHandoff(
    searchParams,
    BASE_HANDOFF_QUERY_KEYS,
    false,
  );
}

export function parseSpecialistLocationGatewayOptionsHandoff(
  searchParams: SpecialistLocationGatewaySearchParamsLike,
) {
  return parseSpecialistLocationGatewayHandoff(
    searchParams,
    SELECTED_HANDOFF_QUERY_KEYS,
    true,
  );
}

export function parseSpecialistLocationGatewayServicesHandoff(
  searchParams: SpecialistLocationGatewaySearchParamsLike,
) {
  const handoff = parseSpecialistLocationGatewayHandoff(
    searchParams,
    new Set([
      ...SELECTED_HANDOFF_QUERY_KEYS,
      ...SPECIALIST_LOCATION_GATEWAY_SERVICES_ALLOWED_QUERY_KEYS,
    ]),
    true,
  );
  if (!handoff.ok) return handoff;

  const values = Object.fromEntries(
    SPECIALIST_LOCATION_GATEWAY_SERVICES_ALLOWED_QUERY_KEYS.map((key) => [
      key,
      searchParams.getAll(key),
    ]),
  ) as Record<
    (typeof SPECIALIST_LOCATION_GATEWAY_SERVICES_ALLOWED_QUERY_KEYS)[number],
    string[]
  >;
  if (Object.values(values).some((entries) => entries.length > 1)) {
    return { ok: false, reason: "invalid-services-query" } as const;
  }

  const single = (
    key: (typeof SPECIALIST_LOCATION_GATEWAY_SERVICES_ALLOWED_QUERY_KEYS)[number],
  ) => values[key][0] ?? null;
  const isIdentifier = (value: string | null) =>
    value === null || isCatalogIdentifier(value);
  const isCanonicalSerializedSelection = (value: string | null) =>
    value === null ||
    (value.length <= MAX_SHARED_SERIALIZED_SELECTION_LENGTH &&
      value === value.trim() &&
      value.length > 0 &&
      !/[\u0000-\u001f\u007f]/u.test(value));
  const canonicalIds = (value: string | null) => {
    if (value === null) return null;
    const ids = value.split(",");
    return ids.every(isCatalogIdentifier) &&
      new Set(ids).size === ids.length
      ? ids
      : null;
  };

  const mode = single("appointment_mode");
  const catalog = single("catalog");
  const groupId = single("group_booking_draft_id");
  const groupVersion = single("group_booking_draft_version");
  const packageIds = single("packages");
  const packageFocus = single("focus_package_id");
  const serviceIds = single("services");
  const serviceFocus = single("focus_service_id");
  const configureService = single("configure_service_id");
  const configureVariant = single("configure_variant_id");
  const serviceAddOns = single("service_addons");
  const serviceBundleAddOns = single("service_bundle_addons");
  const serviceVariants = single("service_variants");
  const selectedServiceIds = canonicalIds(serviceIds);
  const groupPointerPresent = groupId !== null || groupVersion !== null;
  const packageStatePresent =
    catalog === "packages" || packageIds !== null || packageFocus !== null;
  const appointmentStatePresent =
    serviceIds !== null ||
    serviceFocus !== null ||
    configureService !== null ||
    configureVariant !== null ||
    serviceAddOns !== null ||
    serviceBundleAddOns !== null ||
    serviceVariants !== null;
  const nestedServiceStatePresent =
    serviceAddOns !== null ||
    serviceBundleAddOns !== null ||
    serviceVariants !== null;
  const parsedServiceAddOns =
    serviceAddOns === null || selectedServiceIds === null
      ? null
      : parseSpecialistServiceAddOnQuerySelections(
          serviceAddOns,
          selectedServiceIds,
        );
  const parsedServiceBundleAddOns =
    serviceBundleAddOns === null || selectedServiceIds === null
      ? null
      : parseSpecialistServiceBundleAddOnQuerySelections(
          serviceBundleAddOns,
          selectedServiceIds,
        );
  const canonicalServiceVariants = (() => {
    if (serviceVariants === null || selectedServiceIds === null) return null;
    const selected = new Set(selectedServiceIds);
    const seen = new Set<string>();
    const tuples: string[] = [];
    for (const tuple of serviceVariants.split(",")) {
      const separator = tuple.indexOf(":");
      if (
        separator <= 0 ||
        separator !== tuple.lastIndexOf(":") ||
        separator === tuple.length - 1
      ) {
        return null;
      }
      try {
        const serviceId = decodeURIComponent(tuple.slice(0, separator));
        const variantId = decodeURIComponent(tuple.slice(separator + 1));
        if (
          !isCatalogIdentifier(serviceId) ||
          !isCatalogIdentifier(variantId) ||
          !selected.has(serviceId) ||
          seen.has(serviceId) ||
          `${encodeURIComponent(serviceId)}:${encodeURIComponent(variantId)}` !==
            tuple
        ) {
          return null;
        }
        seen.add(serviceId);
        tuples.push(tuple);
      } catch {
        return null;
      }
    }
    return tuples.join(",");
  })();

  if (
    (mode !== null && mode !== "individual" && mode !== "group") ||
    (catalog !== null && catalog !== "services" && catalog !== "packages") ||
    (groupPointerPresent &&
      (!isSpecialistLocationGatewayOpaquePointer(groupId) ||
        groupVersion !== "2" ||
        mode !== "group")) ||
    (mode !== "group" && groupPointerPresent) ||
    (packageStatePresent && mode !== null) ||
    (packageStatePresent && appointmentStatePresent) ||
    (catalog === "packages" && appointmentStatePresent) ||
    (catalog === "services" && (packageIds !== null || packageFocus !== null)) ||
    ((packageIds !== null || packageFocus !== null) && catalog !== "packages") ||
    ((packageIds === null) !== (packageFocus === null)) ||
    (packageIds !== null &&
      (canonicalIds(packageIds)?.length !== 1 ||
        canonicalIds(packageIds)?.[0] !== packageFocus)) ||
    (serviceIds !== null && selectedServiceIds === null) ||
    (serviceFocus !== null &&
      (selectedServiceIds === null ||
        !selectedServiceIds.includes(serviceFocus))) ||
    (configureService !== null &&
      selectedServiceIds?.includes(configureService) === true) ||
    (serviceFocus !== null && configureService !== null) ||
    (nestedServiceStatePresent &&
      (selectedServiceIds === null || selectedServiceIds.length === 0)) ||
    (configureVariant !== null && configureService === null) ||
    !isIdentifier(packageFocus) ||
    !isIdentifier(serviceFocus) ||
    !isIdentifier(configureService) ||
    !isIdentifier(configureVariant) ||
    !isCanonicalSerializedSelection(packageIds) ||
    !isCanonicalSerializedSelection(serviceIds) ||
    !isCanonicalSerializedSelection(serviceAddOns) ||
    !isCanonicalSerializedSelection(serviceBundleAddOns) ||
    !isCanonicalSerializedSelection(serviceVariants) ||
    (serviceAddOns !== null &&
      (parsedServiceAddOns === null ||
        serializeSpecialistServiceAddOnQuerySelections(
          parsedServiceAddOns,
          selectedServiceIds ?? undefined,
        ) !== serviceAddOns)) ||
    (serviceBundleAddOns !== null &&
      (parsedServiceBundleAddOns === null ||
        serializeSpecialistServiceBundleAddOnQuerySelections(
          parsedServiceBundleAddOns,
          selectedServiceIds ?? undefined,
        ) !== serviceBundleAddOns)) ||
    (serviceVariants !== null && canonicalServiceVariants !== serviceVariants)
  ) {
    return { ok: false, reason: "invalid-services-query" } as const;
  }

  return handoff;
}

export function buildSpecialistLocationGatewayLocationPath(input: {
  sourceLocationId: string;
  sourceUserLocationId: string;
}) {
  if (
    !isRouteSegment(input.sourceLocationId) ||
    !isRouteSegment(input.sourceUserLocationId)
  ) {
    return null;
  }
  try {
    const path = `${buildSpecialistProfilePath({
      locationId: input.sourceLocationId,
      userLocationId: input.sourceUserLocationId,
    })}/book/location`;
    const expected = `/locations/${encodeURIComponent(
      input.sourceLocationId,
    )}/specialists/${encodeURIComponent(
      input.sourceUserLocationId,
    )}/book/location`;
    return path === expected ? path : null;
  } catch {
    return null;
  }
}

export function buildSpecialistLocationGatewayOptionsPath(input: {
  sourceLocationId: string;
  sourceUserLocationId: string;
}) {
  const locationPath = buildSpecialistLocationGatewayLocationPath(input);
  return locationPath
    ? `${locationPath.slice(0, -"location".length)}options`
    : null;
}

export function buildSpecialistLocationGatewayServicesPath(input: {
  selectedLocationId: string;
  selectedUserLocationId: string;
}) {
  if (
    !isRouteSegment(input.selectedLocationId) ||
    !isRouteSegment(input.selectedUserLocationId)
  ) {
    return null;
  }
  try {
    const path = buildSpecialistServicesPath({
      locationId: input.selectedLocationId,
      userLocationId: input.selectedUserLocationId,
    });
    const expected = `/locations/${encodeURIComponent(
      input.selectedLocationId,
    )}/specialists/${encodeURIComponent(
      input.selectedUserLocationId,
    )}/services`;
    return path === expected ? path : null;
  } catch {
    return null;
  }
}

export function appendSpecialistLocationGatewayHandoff(input: {
  href: string;
  handoffId: string;
  selectedAssignmentHint?: string | null;
  resultsAppointmentIntentHandoffId?: string | null;
}) {
  if (
    !input.href.startsWith("/") ||
    input.href.startsWith("//") ||
    input.href.includes("\\") ||
    !isSpecialistLocationGatewayOpaquePointer(input.handoffId) ||
    (input.selectedAssignmentHint !== undefined &&
      input.selectedAssignmentHint !== null &&
      !isRouteSegment(
        input.selectedAssignmentHint,
      ))
  ) {
    return null;
  }

  try {
    const origin = "https://widget.volera.invalid";
    const url = new URL(input.href, origin);
    if (
      url.origin !== origin ||
      url.hash !== "" ||
      url.username !== "" ||
      url.password !== "" ||
      url.searchParams.getAll(
        SPECIALIST_LOCATION_GATEWAY_HANDOFF_ID_QUERY_KEY,
      ).length > 0 ||
      url.searchParams.getAll(
        SPECIALIST_LOCATION_GATEWAY_HANDOFF_VERSION_QUERY_KEY,
      ).length > 0 ||
      url.searchParams.getAll(
        SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY,
      ).length > 0 ||
      url.searchParams.getAll(RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY).length >
        0 ||
      url.searchParams.getAll(RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY)
        .length > 0
    ) {
      return null;
    }

    url.searchParams.set(
      SPECIALIST_LOCATION_GATEWAY_HANDOFF_ID_QUERY_KEY,
      input.handoffId,
    );
    url.searchParams.set(
      SPECIALIST_LOCATION_GATEWAY_HANDOFF_VERSION_QUERY_KEY,
      String(SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION),
    );
    if (input.selectedAssignmentHint) {
      url.searchParams.set(
        SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY,
        input.selectedAssignmentHint,
      );
    } else {
      url.searchParams.delete(
        SPECIALIST_LOCATION_GATEWAY_SELECTED_ASSIGNMENT_HINT_QUERY_KEY,
      );
    }
    const gatewayHref = `${url.pathname}${url.search}${url.hash}`;
    return input.resultsAppointmentIntentHandoffId !== undefined &&
      input.resultsAppointmentIntentHandoffId !== null
      ? appendResultsAppointmentIntentHandoffToHref({
          href: gatewayHref,
          handoffId: input.resultsAppointmentIntentHandoffId,
        })
      : gatewayHref;
  } catch {
    return null;
  }
}

export function buildSpecialistLocationGatewayLocationHref(input: {
  sourceLocationId: string;
  sourceUserLocationId: string;
  handoffId: string;
  resultsAppointmentIntentHandoffId?: string | null;
}) {
  const href = buildSpecialistLocationGatewayLocationPath(input);
  return href ? appendSpecialistLocationGatewayHandoff({ ...input, href }) : null;
}

export function buildSpecialistLocationGatewayOptionsHref(input: {
  sourceLocationId: string;
  sourceUserLocationId: string;
  handoffId: string;
  selectedAssignmentHint?: string | null;
  resultsAppointmentIntentHandoffId?: string | null;
}) {
  const href = buildSpecialistLocationGatewayOptionsPath(input);
  return href ? appendSpecialistLocationGatewayHandoff({ ...input, href }) : null;
}


export function buildSpecialistLocationGatewayServicesHref(input: {
  selectedLocationId: string;
  selectedUserLocationId: string;
  handoffId: string;
  selectedAssignmentHint?: string | null;
  appointmentMode?: "group" | "individual" | null;
  groupBookingDraftId?: string | null;
  resultsAppointmentIntentHandoffId?: string | null;
}) {
  const path = buildSpecialistLocationGatewayServicesPath(input);
  if (!path) return null;
  const href = appendSpecialistLocationGatewayHandoff({
    href: path,
    handoffId: input.handoffId,
    selectedAssignmentHint:
      input.selectedAssignmentHint ?? input.selectedUserLocationId,
  });
  if (!href) return null;
  const url = new URL(href, "https://widget.volera.invalid");
  if (input.appointmentMode) {
    url.searchParams.set("appointment_mode", input.appointmentMode);
  }
  if (input.groupBookingDraftId !== undefined && input.groupBookingDraftId !== null) {
    if (
      input.appointmentMode !== "group" ||
      !isSpecialistLocationGatewayOpaquePointer(input.groupBookingDraftId)
    ) {
      return null;
    }
    url.searchParams.set("group_booking_draft_id", input.groupBookingDraftId);
    url.searchParams.set("group_booking_draft_version", "2");
  }
  const gatewayHref = `${url.pathname}${url.search}`;
  return input.resultsAppointmentIntentHandoffId !== undefined &&
    input.resultsAppointmentIntentHandoffId !== null
    ? appendResultsAppointmentIntentHandoffToHref({
        href: gatewayHref,
        handoffId: input.resultsAppointmentIntentHandoffId,
      })
    : gatewayHref;
}
